import { useEffect, useState } from 'react'
import { Smartphone, Monitor, X, Share } from 'lucide-react'
import { isNativeApp } from '../lib/native'

const DISMISS_KEY = 'mondalon_pwa_dismissed_at'
const DISMISS_DAYS = 14

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// 'ios' = hint manuel « écran d'accueil » ; 'mac' = hint manuel « Ajouter au Dock »
// (Safari desktop) ; null = invite d'install native (Chrome/Edge, via beforeinstallprompt).
type Hint = 'ios' | 'mac' | null

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [hint, setHint] = useState<Hint>(null)

  useEffect(() => {
    // Jamais dans l'app native : elle est déjà "installée".
    if (isNativeApp()) return
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0)
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400 * 1000) return

    // Déjà installé (standalone display-mode) ?
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    if (isStandalone) return

    const ua = navigator.userAgent
    // iOS : iPhone/iPod + iPad iPadOS (qui se déguise en Mac tactile)
    const isIOS =
      /iPhone|iPod|iPad/i.test(ua) ||
      (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
    // Safari sur macOS desktop : pas de beforeinstallprompt → geste manuel « Ajouter au Dock »
    const isMacSafari =
      /Macintosh/.test(ua) &&
      navigator.maxTouchPoints === 0 &&
      /Safari/.test(ua) &&
      !/Chrome|Chromium|Edg|OPR/.test(ua)

    if (isIOS) {
      setHint('ios')
      setShow(true)
      return
    }
    if (isMacSafari) {
      setHint('mac')
      setShow(true)
      return
    }

    // Chrome/Edge (mobile ET desktop) : invite d'install native
    function handler(e: Event) {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setShow(false)
  }

  async function install() {
    if (!deferred) return
    await deferred.prompt()
    const choice = await deferred.userChoice
    if (choice.outcome === 'accepted') {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    }
    setShow(false)
    setDeferred(null)
  }

  if (!show) return null

  const isMac = hint === 'mac'
  const title = isMac ? 'Ajoute mondalon à ton Dock' : 'Ajoute mondalon à ton écran'

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40">
      <div className="bg-white rounded-2xl border border-canne/20 shadow-xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-terre/10 text-terre flex items-center justify-center shrink-0">
          {isMac ? <Monitor className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-nuit text-sm mb-0.5">{title}</p>
          {hint === 'ios' ? (
            <p className="text-xs text-nuit/70 leading-relaxed">
              Clique sur <Share className="w-3 h-3 inline -mt-0.5 mx-0.5" /> dans ton navigateur, puis « Ajouter à l'écran d'accueil ».
            </p>
          ) : hint === 'mac' ? (
            <p className="text-xs text-nuit/70 leading-relaxed">
              Dans Safari, menu <strong>Fichier</strong> → «&nbsp;<strong>Ajouter au Dock</strong>&nbsp;» pour retrouver mondalon comme une app.
            </p>
          ) : (
            <p className="text-xs text-nuit/70 leading-relaxed">
              Pour retrouver mondalon comme une app, sans passer par ton navigateur.
            </p>
          )}
          {hint === null && (
            <button
              onClick={install}
              className="mt-2 px-3 py-1.5 rounded-xl bg-terre text-creme font-semibold text-xs hover:bg-terre/90"
            >
              Installer
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 text-nuit/40 hover:text-nuit"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
